// ═══════════════════════════════════════════════════════════════
// Forms — Analytics
// Response volumes, template performance, status breakdowns
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Badge, Progress,
  Table,
} from '../../components/gab/GabComponents';
import {
  IconForms, IconCheck, IconClipboardList, IconChartBar,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useFormsStore } from '../../store/formsStore';
import { semantic, primary } from '../../lib/colors';

// ── Helpers ──────────────────────────────────────────────────

function StatCard({
  label, value, sub, color, icon,
}: { label: string; value: string | number; sub?: string; color: string; icon: React.ReactNode }) {
  return (
    <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${color}` }}>
      <Group justify="center" gap="xs" mb={4}>
        <span style={{ color }}>{icon}</span>
        <Text size="xs" c="dimmed">{label}</Text>
      </Group>
      <Text fz={28} fw={700} style={{ color }}>{value}</Text>
      {sub && <Text size="xs" c="dimmed">{sub}</Text>}
    </Paper>
  );
}

// ── Component ────────────────────────────────────────────────

export default function FormAnalytics() {
  const { templates, responses, loadTemplates, loadResponses } = useFormsStore();

  useEffect(() => {
    loadTemplates();
    loadResponses();
  }, []);

  // ── Metrics ───────────────────────────────────────────────

  const publishedTemplates = useMemo(() =>
    templates.filter(t => t.status === 'published'),
    [templates],
  );

  const totalResponses = responses.length;

  // Responses by status
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    responses.forEach(r => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [responses]);

  const reviewedPct = totalResponses > 0
    ? Math.round(
        ((statusCounts['reviewed'] || 0) + (statusCounts['complete'] || 0)) /
        totalResponses * 100,
      )
    : 0;

  // Responses per template
  const byTemplate = useMemo(() => {
    return templates
      .map(t => ({
        ...t,
        count: responses.filter(r => String(r.templateId) === String(t.id)).length,
      }))
      .filter(t => t.count > 0 || t.status === 'published')
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  }, [templates, responses]);

  const maxCount = Math.max(...byTemplate.map(t => t.count), 1);

  // Responses last 7 days
  const last7Days = useMemo(() => {
    const days: { label: string; count: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const day = dayjs().subtract(i, 'day');
      const count = responses.filter(r =>
        r.submittedAt && dayjs(r.submittedAt).isSame(day, 'day'),
      ).length;
      days.push({ label: day.format('ddd'), count });
    }
    return days;
  }, [responses]);

  const maxDay = Math.max(...last7Days.map(d => d.count), 1);

  // Average responses per published template
  const avgResponses = publishedTemplates.length > 0
    ? (totalResponses / publishedTemplates.length).toFixed(1)
    : '0';

  const STATUS_COLORS: Record<string, string> = {
    new: primary[700],
    reviewed: '#2563eb',
    complete: semantic.success,
    pending: semantic.warning,
  };

  // ── Render ───────────────────────────────────────────────

  return (
    <div>
      <Title order={3} c="black" mb="md">Form Analytics</Title>

      {/* Stat cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <StatCard
          label="Published Forms"
          value={publishedTemplates.length}
          sub={`${templates.length} total templates`}
          color={primary[700]}
          icon={<IconForms size={16} />}
        />
        <StatCard
          label="Total Responses"
          value={totalResponses}
          sub={`avg ${avgResponses} per form`}
          color={semantic.success}
          icon={<IconClipboardList size={16} />}
        />
        <StatCard
          label="Reviewed"
          value={`${reviewedPct}%`}
          sub={`${(statusCounts['reviewed'] || 0) + (statusCounts['complete'] || 0)} responses`}
          color="var(--color-info)"
          icon={<IconCheck size={16} />}
        />
        <StatCard
          label="New / Unreviewed"
          value={statusCounts['new'] || 0}
          sub="awaiting review"
          color={semantic.warning}
          icon={<IconChartBar size={16} />}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md" spacing="sm">
        {/* Responses last 7 days */}
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb="sm">
            <IconChartBar size={16} color={primary[700]} />
            <Text fw={600} size="sm">Responses — Last 7 Days</Text>
          </Group>
          {last7Days.every(d => d.count === 0) ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No responses in the last 7 days.</Text>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140, paddingBottom: 4 }}>
              {last7Days.map(d => (
                <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Text size="xs" c="dimmed">{d.count > 0 ? d.count : ''}</Text>
                  <div style={{
                    width: '100%',
                    height: Math.max((d.count / maxDay) * 110, d.count > 0 ? 4 : 0),
                    background: d.count > 0 ? primary[700] : 'var(--frost)',
                    borderRadius: '4px 4px 0 0',
                    minHeight: 2,
                  }} />
                  <Text size="xs" c="dimmed">{d.label}</Text>
                </div>
              ))}
            </div>
          )}
        </Paper>

        {/* Status breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Response Status Breakdown</Text>
          {totalResponses === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No responses yet.</Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(statusCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => {
                  const pct = Math.round((count / totalResponses) * 100);
                  return (
                    <div key={status}>
                      <Group justify="space-between" mb={2}>
                        <Badge
                          size="xs"
                          tt="capitalize"
                          style={{
                            background: `${STATUS_COLORS[status] ?? '#6b7280'}22`,
                            color: STATUS_COLORS[status] ?? '#6b7280',
                          }}
                        >
                          {status}
                        </Badge>
                        <Text size="xs" c="dimmed">{count} ({pct}%)</Text>
                      </Group>
                      <Progress
                        value={pct}
                        size="sm"
                        color={STATUS_COLORS[status] ? undefined : 'gray'}
                        styles={STATUS_COLORS[status] ? {
                          bar: { background: STATUS_COLORS[status] },
                        } : undefined}
                      />
                    </div>
                  );
                })}
            </div>
          )}
        </Paper>
      </SimpleGrid>

      {/* Responses by template */}
      <Paper p="md" radius="sm" withBorder>
        <Text fw={600} size="sm" mb="sm">Responses by Form Template</Text>
        {byTemplate.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">No published templates yet.</Text>
        ) : (
          <div>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="sm">
              {byTemplate.slice(0, 8).map(t => (
                <div key={t.id}>
                  <Group justify="space-between" mb={2}>
                    <Group gap={6}>
                      <Text size="sm" fw={500} lineClamp={1} style={{ maxWidth: 200 }}>
                        {t.title || t.templateName || 'Untitled'}
                      </Text>
                      <Badge size="xs" color={t.status === 'published' ? 'green' : 'gray'} tt="capitalize">
                        {t.status}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">{t.count}</Text>
                  </Group>
                  <Progress
                    value={(t.count / maxCount) * 100}
                    size="sm"
                    color="blue"
                  />
                </div>
              ))}
            </SimpleGrid>

            {byTemplate.length > 8 && (
              <Table.ScrollContainer minWidth={400} mt="sm">
                <Table verticalSpacing={4} fz="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Template</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Responses</Table.Th>
                      <Table.Th>New</Table.Th>
                      <Table.Th>Reviewed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {byTemplate.slice(8).map(t => {
                      const tResps = responses.filter(r => String(r.templateId) === String(t.id));
                      const newCount = tResps.filter(r => r.status === 'new').length;
                      const doneCount = tResps.filter(r => r.status === 'reviewed' || r.status === 'complete').length;
                      return (
                        <Table.Tr key={t.id}>
                          <Table.Td>{t.title || t.templateName || 'Untitled'}</Table.Td>
                          <Table.Td>
                            <Badge size="xs" color={t.status === 'published' ? 'green' : 'gray'} tt="capitalize">
                              {t.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>{t.count}</Table.Td>
                          <Table.Td>{newCount || '—'}</Table.Td>
                          <Table.Td>{doneCount || '—'}</Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </div>
        )}
      </Paper>
    </div>
  );
}
